import { Children, isValidElement, useMemo, useRef, useState } from 'react';
import { chakraComponents } from 'chakra-react-select';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Box, Flex } from '@chakra-ui/react';

import VCheckbox from './VCheckbox.jsx';
import VSelect from './VSelect.jsx';

const optionHeight = 36;
const virtualizeAboveCount = 50;
const defaultSummariseSelectionAboveCount = 500;
const inputActionsThatKeepText = ['menu-close', 'input-blur'];
const clearSearchLabel = 'Clear search';

const getGroupOptions = (item) => {
  const groupOptions = item?.props?.data?.options;
  return Array.isArray(groupOptions) ? groupOptions : null;
};

const countOptions = (items) =>
  items.reduce((sum, item) => {
    const groupOptions = getGroupOptions(item);
    return sum + (groupOptions ? groupOptions.length : 1);
  }, 0);

const flattenChildren = (children) => {
  const rows = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }
    if (getGroupOptions(child)) {
      rows.push({ type: 'heading', groupElement: child });
      Children.forEach(child.props.children, (optionElement) => {
        if (isValidElement(optionElement)) {
          rows.push({ type: 'option', element: optionElement });
        }
      });
    } else {
      rows.push({ type: 'option', element: child });
    }
  });
  return rows;
};

const getAllOptions = (options) =>
  options.flatMap((option) => (Array.isArray(option.options) ? option.options : [option]));

const getGroupCheckedState = (allSelected, someSelected) => {
  if (allSelected) {
    return true;
  }
  if (someSelected) {
    return 'indeterminate';
  }
  return false;
};

const SelectAllHeading = (props) => {
  const { selectProps } = props;

  if (!selectProps.isMulti || !selectProps.showSelectAll) {
    return null;
  }

  const allOptions = getAllOptions(selectProps.options);
  const selectedCount = Array.isArray(selectProps.value) ? selectProps.value.length : 0;
  const allSelected = allOptions.length > 0 && selectedCount === allOptions.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleAll = () => {
    selectProps.onChange(allSelected ? [] : allOptions, {
      action: allSelected ? 'deselect-option' : 'select-option',
    });
  };

  return (
    <chakraComponents.GroupHeading
      selectProps={props.selectProps}
      theme={props.theme}
      cx={props.cx}
      getStyles={props.getStyles}
      getClassNames={props.getClassNames}
    >
      <Flex
        role="checkbox"
        aria-checked={someSelected ? 'mixed' : allSelected}
        align="center"
        justifyContent="space-between"
        gap="2"
        width="100%"
        cursor="pointer"
        onMouseDown={(event) => event.preventDefault()}
        onClick={toggleAll}
      >
        <Box>Select All</Box>
        <VCheckbox checked={getGroupCheckedState(allSelected, someSelected)} pointerEvents="none" />
      </Flex>
    </chakraComponents.GroupHeading>
  );
};

const VirtualizedInnerList = ({ children, maxHeight, innerRef, ...rest }) => {
  const scrollRef = useRef(null);
  const isMulti = rest.selectProps?.isMulti;
  const showSelectAll = rest.selectProps?.showSelectAll;

  const rows = useMemo(() => {
    const optionRows = flattenChildren(children);
    return isMulti && showSelectAll ? [{ type: 'select-all' }, ...optionRows] : optionRows;
  }, [children, isMulti, showSelectAll]);

  const setRefs = (element) => {
    scrollRef.current = element;
    if (typeof innerRef === 'function') {
      innerRef(element);
    }
  };

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => optionHeight,
    getScrollElement: () => scrollRef.current,
    overscan: 20,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <chakraComponents.MenuList {...rest}>
      <div
        ref={setRefs}
        style={{
          height: `${maxHeight}px`,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <div style={{ height: `${totalSize}px`, position: 'relative' }}>
          {virtualItems.map((virtualItem) => {
            const row = rows[virtualItem.index];
            const GroupHeading = row?.groupElement?.props?.Heading ?? chakraComponents.GroupHeading;
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {row?.type === 'select-all' ? (
                  <SelectAllHeading {...rest} />
                ) : row?.type === 'heading' ? (
                  <GroupHeading
                    {...row.groupElement.props.headingProps}
                    selectProps={row.groupElement.props.selectProps}
                    theme={row.groupElement.props.theme}
                    cx={row.groupElement.props.cx}
                    getStyles={row.groupElement.props.getStyles}
                    getClassNames={row.groupElement.props.getClassNames}
                  >
                    {row.groupElement.props.label}
                  </GroupHeading>
                ) : (
                  row?.element
                )}
              </div>
            );
          })}
        </div>
      </div>
    </chakraComponents.MenuList>
  );
};

const VirtualizedMenuList = (props) => {
  const items = Array.isArray(props.children) ? props.children : [];

  if (countOptions(items) < virtualizeAboveCount) {
    return (
      <chakraComponents.MenuList {...props}>
        <SelectAllHeading {...props} />
        {props.children}
      </chakraComponents.MenuList>
    );
  }

  return <VirtualizedInnerList {...props}>{items}</VirtualizedInnerList>;
};

const MenuListWithSelectAll = (props) => (
  <chakraComponents.MenuList {...props}>
    <SelectAllHeading {...props} />
    {props.children}
  </chakraComponents.MenuList>
);

const SummarisingValueContainer = ({ children, ...props }) => {
  const selectedCount = props.getValue().length;
  const threshold =
    props.selectProps.summariseSelectionAboveCount ?? defaultSummariseSelectionAboveCount;

  if (selectedCount <= threshold) {
    return <chakraComponents.ValueContainer {...props}>{children}</chakraComponents.ValueContainer>;
  }

  const input = Array.isArray(children) ? children[children.length - 1] : children;

  return (
    <chakraComponents.ValueContainer {...props}>
      <Box>{selectedCount} items selected</Box>
      {input}
    </chakraComponents.ValueContainer>
  );
};

const ClearSearchIndicatorsContainer = (props) => {
  const { selectProps, cx, children } = props;

  const clearSearch = (event) => {
    event.preventDefault();
    selectProps.onInputChange('', { action: 'input-change' });
  };

  return (
    <chakraComponents.IndicatorsContainer {...props}>
      {selectProps.inputValue && (
        <chakraComponents.ClearIndicator
          cx={cx}
          selectProps={selectProps}
          innerProps={{ 'aria-label': clearSearchLabel, onMouseDown: clearSearch }}
        />
      )}
      {children}
    </chakraComponents.IndicatorsContainer>
  );
};

const chakraStyles = {
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),

  indicatorsContainer: (provided) => ({
    ...provided,
    alignSelf: 'flex-start',
    height: '2.5rem',
    alignItems: 'center',
    paddingInlineStart: '0',
  }),

  clearIndicator: (provided) => ({
    ...provided,
    paddingInlineStart: '0',
  }),

  dropdownIndicator: (provided) => ({
    ...provided,
    background: 'transparent',
  }),

  control: (provided) => ({
    ...provided,
    borderRadius: 'var(--chakra-radii-md)',
    backgroundColor: 'var(--chakra-colors-white)',
  }),

  valueContainer: (provided, { selectProps }) =>
    selectProps.isMulti
      ? {
          ...provided,
          maxHeight: '4.8rem',
          overflowY: 'auto',
          paddingInlineEnd: '0',
        }
      : provided,

  menu: (provided) => ({
    ...provided,
    zIndex: 10,
  }),

  option: (provided) => ({
    ...provided,
    cursor: 'pointer',
    _highlighted: { background: 'transparent' },
    _hover: { background: 'bg.emphasized/60' },
  }),

  groupHeading: (provided, props) => ({
    ...provided,
    fontWeight: '700',
    color: 'var(--chakra-colors-fg-muted)',
    background: 'var(--chakra-colors-bg-panel)',
    ...(props?.selectProps?.isMulti
      ? {
          cursor: 'pointer',
          _hover: { background: 'var(--chakra-colors-bg-muted)' },
        }
      : {}),
  }),
};

export const mergeChakraStyles = (base = {}, override = {}) => {
  const keys = new Set([...Object.keys(base), ...Object.keys(override)]);
  const merged = {};
  keys.forEach((key) => {
    const baseStyle = base[key];
    const overrideStyle = override[key];
    merged[key] = (provided, state) => {
      const withBase = baseStyle ? baseStyle(provided, state) : provided;
      return overrideStyle ? overrideStyle(withBase, state) : withBase;
    };
  });
  return merged;
};

const CheckableGroupHeading = (props) => {
  const { data, selectProps } = props;
  const groupOptions = Array.isArray(data?.options) ? data.options : [];
  const selectedOptions = Array.isArray(selectProps.value) ? selectProps.value : [];
  const selectedValues = new Set(selectedOptions.map((option) => option.value));
  const selectedInGroup = groupOptions.filter((option) => selectedValues.has(option.value));
  const allSelected = groupOptions.length > 0 && selectedInGroup.length === groupOptions.length;
  const someSelected = selectedInGroup.length > 0 && !allSelected;

  const toggleGroup = () => {
    const nextOptions = allSelected
      ? selectedOptions.filter(
          (option) => !groupOptions.some((groupOption) => groupOption.value === option.value)
        )
      : [...selectedOptions, ...groupOptions.filter((option) => !selectedValues.has(option.value))];

    selectProps.onChange(nextOptions, {
      action: allSelected ? 'deselect-option' : 'select-option',
    });
  };

  return (
    <chakraComponents.GroupHeading {...props}>
      <Flex
        align="center"
        justifyContent="space-between"
        gap="2"
        width="100%"
        cursor="pointer"
        onMouseDown={(event) => event.preventDefault()}
        onClick={toggleGroup}
      >
        <Box>{data.label}</Box>
        <VCheckbox checked={getGroupCheckedState(allSelected, someSelected)} pointerEvents="none" />
      </Flex>
    </chakraComponents.GroupHeading>
  );
};

const VMultiSelect = ({
  onValueChange,
  multiple = true,
  virtualized = false,
  groupBy,
  showSelectedOptions,
  showSelectAll = false,
  clearableSearch = false,
  summariseSelectionAboveCount = defaultSummariseSelectionAboveCount,
  chakraStyles: chakraStylesOverride,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = (nextInputValue, { action }) => {
    if (inputActionsThatKeepText.includes(action)) {
      return;
    }
    setInputValue(nextInputValue);
  };

  const handleChange = (selected, { action }) => {
    if (action === 'clear') {
      setInputValue('');
    }
    const items = [].concat(selected || []);
    onValueChange?.({
      value: items.map((item) => item.value),
      items,
    });
  };

  const componentOverrides = useMemo(() => {
    const overrides = {};
    if (virtualized) {
      overrides.MenuList = VirtualizedMenuList;
    } else if (multiple && showSelectAll) {
      overrides.MenuList = MenuListWithSelectAll;
    }
    if (groupBy) {
      overrides.GroupHeading = multiple ? CheckableGroupHeading : chakraComponents.GroupHeading;
    }
    if (multiple) {
      overrides.ValueContainer = SummarisingValueContainer;
    }
    if (clearableSearch) {
      overrides.IndicatorsContainer = ClearSearchIndicatorsContainer;
    }
    return Object.keys(overrides).length > 0 ? overrides : undefined;
  }, [virtualized, groupBy, multiple, showSelectAll, clearableSearch]);

  return (
    <VSelect
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      multiple={multiple}
      groupBy={groupBy}
      showSelectAll={showSelectAll}
      summariseSelectionAboveCount={summariseSelectionAboveCount}
      closeMenuOnSelect={!multiple}
      blurInputOnSelect={!multiple}
      hideSelectedOptions={showSelectedOptions === undefined ? undefined : !showSelectedOptions}
      chakraStyles={mergeChakraStyles(chakraStyles, chakraStylesOverride)}
      components={componentOverrides}
      {...props}
    />
  );
};

export default VMultiSelect;
